import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LegalPage, LegalSection, Placeholder } from '@/components/generals/legal'
import { company } from '@/constants/site'

const Datenschutz: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Datenschutz'
                description='Datenschutzerklärung der Gashi Holding GmbH: welche Daten diese Website verarbeitet, zu welchem Zweck und welche Rechte Sie haben.'
                path='/datenschutz'
            />

            <LegalPage
                title='Datenschutzerklärung'
                updated='November 2026'
                intro='Wir verarbeiten personenbezogene Daten nur, soweit es für den Betrieb dieser Website und die Bearbeitung Ihrer Anfragen erforderlich ist. Hier erfahren Sie, welche Daten das sind und welche Rechte Sie haben.'
            >
                <LegalSection title='1. Verantwortlicher'>
                    <p>
                        {company.legalName}
                        <br />
                        {company.street}
                        <br />
                        {company.postalCode} {company.city}
                        <br />
                        {company.country}
                    </p>

                    <p>
                        Vertreten durch die Geschäftsführung: <Placeholder>Name ergänzen</Placeholder>
                        <br />
                        E-Mail: <Placeholder>E-Mail-Adresse ergänzen</Placeholder>
                    </p>
                </LegalSection>

                <LegalSection title='2. Überblick'>
                    <p>
                        Diese Website setzt keine Cookies zu Analyse- oder Werbezwecken ein und bindet keine Inhalte von Drittanbietern wie Kartendienste, Videoplattformen oder externe Schriftdienste ein. Personenbezogene Daten fallen beim Aufruf der Website, bei der Besucherstatistik und bei Anfragen über das Kontaktformular an.
                    </p>
                </LegalSection>

                <LegalSection title='3. Bereitstellung der Website und Server-Logdateien'>
                    <p>
                        Diese Website wird über die Website-Plattform Modulify bereitgestellt, die von der Azwedo L.L.C. betrieben wird. Beim Aufruf der Website verarbeitet der Anbieter in unserem Auftrag technisch notwendige Daten, die Ihr Browser übermittelt, insbesondere IP-Adresse, Datum und Uhrzeit des Zugriffs, die aufgerufene Seite, die zuvor besuchte Seite sowie Browser und Betriebssystem.
                    </p>

                    <p>
                        Die Verarbeitung ist erforderlich, um die Website auszuliefern und ihre Sicherheit und Stabilität zu gewährleisten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse liegt im sicheren und fehlerfreien Betrieb der Website. Server-Logdateien werden vom Anbieter nach einer begrenzten Zeit automatisch gelöscht.
                    </p>

                    <p>
                        <Placeholder>Bitte prüfen und ergänzen: Vertrag zur Auftragsverarbeitung mit dem Anbieter und Serverstandort der Website</Placeholder>
                    </p>
                </LegalSection>

                <LegalSection title='4. Besucherstatistik ohne Cookies'>
                    <p>
                        Um zu verstehen, wie unsere Website genutzt wird, verwenden wir die integrierte Besucherstatistik der Plattform. Sie arbeitet ohne Cookies und erfasst Seitenaufrufe, die verweisende Quelle, das Land sowie Browser, Betriebssystem und Gerätetyp. Es werden keine Nutzerprofile über verschiedene Websites hinweg gebildet.
                    </p>

                    <p>
                        Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse liegt darin, unser Angebot zu verbessern.
                    </p>
                </LegalSection>

                <LegalSection title='5. Kontaktformular'>
                    <p>
                        Wenn Sie uns über das Kontaktformular schreiben, verarbeiten wir Ihr Anliegen, Ihren Namen, Ihre E-Mail-Adresse, optional Ihr Unternehmen sowie Ihre Nachricht, um Ihre Anfrage zu bearbeiten und zu beantworten. Die Angaben werden in der Datenbank dieser Website gespeichert und uns zusätzlich über den E-Mail-Dienst der Plattform zugestellt.
                    </p>

                    <p>
                        Zum Schutz vor Missbrauch wird Ihre IP-Adresse kurzzeitig im Arbeitsspeicher verarbeitet, um die Zahl der Anfragen pro Absender zu begrenzen. Sie wird dabei nicht dauerhaft gespeichert.
                    </p>

                    <p>
                        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, soweit Ihre Anfrage auf einen Vertrag oder vorvertragliche Maßnahmen zielt, im Übrigen Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse liegt in der Beantwortung Ihrer Anfrage. Wir löschen die Daten, sobald Ihre Anfrage abschließend bearbeitet ist und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
                    </p>
                </LegalSection>

                <LegalSection title='6. Kontakt per E-Mail'>
                    <p>
                        Wenn Sie uns per E-Mail schreiben, verarbeiten wir die Angaben aus Ihrer Nachricht, um Ihr Anliegen zu bearbeiten. Für Rechtsgrundlage und Speicherdauer gilt das zum Kontaktformular Gesagte entsprechend.
                    </p>
                </LegalSection>

                <LegalSection title='7. Schriftarten'>
                    <p>
                        Die verwendeten Schriftarten werden direkt von unserer Website geladen. Beim Aufruf der Website wird keine Verbindung zu Servern von Google oder anderen Schriftanbietern hergestellt.
                    </p>
                </LegalSection>

                <LegalSection title='8. Empfänger und Übermittlung in Drittländer'>
                    <p>
                        Empfänger Ihrer Daten sind neben uns die genannten technischen Dienstleister, die in unserem Auftrag tätig sind. Nach Angaben des Plattformbetreibers befinden sich dessen Hauptsysteme in den USA, sodass eine Übermittlung in ein Drittland nicht ausgeschlossen werden kann.
                    </p>

                    <p>
                        <Placeholder>Bitte prüfen und ergänzen: Garantien für Übermittlungen in Drittländer, zum Beispiel EU-Standardvertragsklauseln</Placeholder>
                    </p>
                </LegalSection>

                <LegalSection title='9. Ihre Rechte'>
                    <p>
                        Sie haben im Rahmen der gesetzlichen Vorgaben jederzeit das Recht auf:
                    </p>

                    <ul>
                        <li>Auskunft über Ihre bei uns gespeicherten Daten (Art. 15 DSGVO)</li>
                        <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
                        <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
                        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                        <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
                        <li>Beschwerde bei einer Datenschutz-Aufsichtsbehörde (Art. 77 DSGVO), zum Beispiel beim Hessischen Beauftragten für Datenschutz und Informationsfreiheit</li>
                    </ul>

                    <p>
                        <strong className='font-semibold text-foreground'>Widerspruchsrecht:</strong> Soweit wir Daten auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO verarbeiten, können Sie dieser Verarbeitung aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit widersprechen (Art. 21 DSGVO).
                    </p>

                    <p>
                        Zur Ausübung Ihrer Rechte genügt eine formlose Nachricht, zum Beispiel über unser <Link href='/#kontakt'>Kontaktformular</Link>.
                    </p>
                </LegalSection>

                <LegalSection title='10. Datensicherheit'>
                    <p>
                        Diese Website nutzt eine verschlüsselte Verbindung (TLS). Sie erkennen sie an „https://“ und dem Schloss-Symbol in der Adresszeile Ihres Browsers.
                    </p>
                </LegalSection>

                <LegalSection title='11. Änderungen dieser Datenschutzerklärung'>
                    <p>
                        Wir passen diese Datenschutzerklärung an, sobald sich die Website oder die rechtlichen Anforderungen ändern. Es gilt die jeweils hier veröffentlichte Fassung.
                    </p>
                </LegalSection>
            </LegalPage>
        </Layout>
    )
}

export default Datenschutz
